import Image from 'next/image'

import errorImage from '@/assets/error-imagen.webp'
import { Button } from '@/app/components/atoms/Button'
import { PageContainer } from '@/app/components/atoms/PageContainer'
import { PageAnalyticsProvider } from '@/app/context/analytics'
import { NOT_SET } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import type { PageContext } from '@/app/lib/analytics/types'

import {
  buildAccessibleHeading,
  buildDescription,
  buildErrorPageTitle,
  HOME_LINK_LABEL,
  TITLE,
} from './ErrorPageFallback.constants'
import { ErrorPageFallbackHomeButton } from './ErrorPageFallbackHomeButton'
import styles from './ErrorPageFallback.module.scss'

interface ErrorPageFallbackShellProps {
  readonly title: string
  readonly code: number | string
  /**
   * Destino fijo de "Ir a página de inicio". Lo usan los canales (home del
   * canal), que no montan `CityProvider`; sin él, el botón enlaza a la home de
   * la provincia activa (`ErrorPageFallbackHomeButton`).
   */
  readonly homeHref?: string
  /** Texto propio en lugar del genérico con el código (p. ej. 403 de canales expertos). */
  readonly description?: string
}

/**
 * Contexto de página de las páginas de error (`analytic-variables.md` →
 * `page_type=error`): `page_subtype` es el código de error (`404`, `500`…) y
 * `page_hierarchy` `not_set` (decisión de producto, sin jerarquía de error).
 */
function buildErrorPageContext(code: number | string): PageContext {
  return {
    page_title: slugify(buildErrorPageTitle(code)),
    page_type: 'error',
    page_subtype: slugify(String(code)),
    page_hierarchy: NOT_SET,
    page_brand: NOT_SET,
    page_brand_type: NOT_SET,
  }
}

/**
 * Banda visual de `ErrorPageFallback` (imagen de fondo + título + descripción
 * + botón "Ir a página de inicio"), extraída a su propio componente para que
 * también pueda usarla `(general-web)/error.tsx` — un error boundary de
 * Next.js, que **debe** ser Client Component (`node_modules/next/dist/docs/
 * .../error.md`, "Error boundaries must be Client Components"). Ese archivo
 * no puede importar `ErrorPageFallback` directamente: arrastraría
 * `MODULES_MAP` (`@/app/components/modules/index.ts`) a su bundle de
 * cliente, y varios de esos módulos son Server Components asíncronos que no
 * pueden formar parte de un árbol de Cliente. Este componente, en cambio, solo
 * usa piezas ya seguras en cliente (`next/image`, `PageContainer`,
 * `ErrorPageFallbackHomeButton`, que ya es `'use client'`) — sin `MODULES_MAP`.
 *
 * Sin `'use client'` propio: al no depender de nada exclusivo de servidor,
 * funciona igual importado desde un Server Component (`ErrorPageFallback`,
 * `not-found.tsx`) o desde uno de Cliente (`error.tsx`).
 */
export function ErrorPageFallbackShell({
  title,
  code,
  homeHref,
  description,
}: ErrorPageFallbackShellProps) {
  return (
    // Único punto por el que pasan TODAS las páginas de error (404, 5xx, 403 y
    // `error.tsx` de ambos segmentos): emite un único `page_view` con `page_type=error`.
    <PageAnalyticsProvider context={buildErrorPageContext(code)}>
      <div className={styles.root}>
        <div className={styles.background} aria-hidden="true">
          <Image src={errorImage} alt="" fill sizes="100vw" className={styles.backgroundImage} />
          <div className={styles.overlay} />
        </div>
        <div className={styles.foreground}>
          <PageContainer>
            <div className={styles.content}>
              <div className={styles.textBlock}>
                <h1 className={styles.heading}>
                  {title === TITLE ? (
                    <>
                      <span aria-hidden="true">{title}</span>
                      <span className={styles.srOnly}>{buildAccessibleHeading(code)}</span>
                    </>
                  ) : (
                    title
                  )}
                </h1>
                <p className={styles.description}>{description ?? buildDescription(code)}</p>
              </div>
              {homeHref ? (
                <Button href={homeHref} variant="filled-inverse" shape="block" size="m">
                  {HOME_LINK_LABEL}
                </Button>
              ) : (
                <ErrorPageFallbackHomeButton />
              )}
            </div>
          </PageContainer>
        </div>
      </div>
    </PageAnalyticsProvider>
  )
}
